import {
  NoteType,
  NoteUpsertPayload,
  FetchNotesType,
} from "features/calendar-new/types";
import { GeneratedSchedugramServerClient } from "./generated-schedugram-server-client";
import type {
  components,
  paths,
} from "./generated/schedugram-server-openapi-types";

const generatedSchedugramServerClient =
  new GeneratedSchedugramServerClient<paths>();

// Production currently uses qs's default indexed-array limit of 20. Staying
// at that boundary keeps a batch as an array even if an intermediary rewrites
// repeated parameters to the indexed form.
const NOTES_ACCOUNT_BATCH_SIZE = 20;
const NOTES_REQUEST_CONCURRENCY = 4;

type CalendarNoteRequestBody = components["schemas"]["CalendarNoteRequestBody"];
type CalendarNoteResponse = components["schemas"]["CalendarNoteResponse"];

export async function fetchNotes(
  options: FetchNotesType,
  signal?: AbortSignal
): Promise<{
  data: NoteType[];
}> {
  const accountBatches = chunk(options.accounts, NOTES_ACCOUNT_BATCH_SIZE);
  if (accountBatches.length === 0) return { data: [] };

  const responses = await parallelMap(
    accountBatches,
    NOTES_REQUEST_CONCURRENCY,
    async (accounts) => {
      const result = await generatedSchedugramServerClient.client.GET(
        "/calendars/notes",
        {
          params: {
            query: { ...options, accounts },
          },
          // The server's query parser and TSOA validator expect repeated
          // scalar parameters. Do not use indexed forms such as accounts[0],
          // which arrive as a nested object and fail validation.
          querySerializer: (query) => serializeNotesQuery(query),
          signal,
        }
      );

      return generatedSchedugramServerClient.unwrap(result);
    }
  );

  return {
    data: deduplicateNotes(responses.flat()).map((note) => toNote(note)),
  };
}

function serializeNotesQuery(options: Partial<FetchNotesType>): string {
  const query = new URLSearchParams();
  options.accounts?.forEach((account) => query.append("accounts", account));
  if (options.start !== undefined) query.set("start", String(options.start));
  if (options.end !== undefined) query.set("end", String(options.end));
  return query.toString();
}

function chunk<T>(values: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(values.length / size) }, (_, index) =>
    values.slice(index * size, (index + 1) * size)
  );
}

async function parallelMap<T, TResult>(
  values: T[],
  concurrency: number,
  mapper: (value: T) => Promise<TResult>
): Promise<TResult[]> {
  const results: TResult[] = [];
  results.length = values.length;
  let nextIndex = 0;

  await Promise.all(
    Array.from({ length: Math.min(concurrency, values.length) }, async () => {
      while (nextIndex < values.length) {
        const index = nextIndex++;
        results[index] = await mapper(values[index]);
      }
    })
  );

  return results;
}

function deduplicateNotes(
  notes: CalendarNoteResponse[]
): CalendarNoteResponse[] {
  const seen = new Set<string>();

  return notes.filter((note) => {
    if (!note._id) return true;
    if (seen.has(note._id)) return false;
    seen.add(note._id);
    return true;
  });
}

export async function createNote(note: NoteUpsertPayload): Promise<{
  data: NoteType;
}> {
  const result = await generatedSchedugramServerClient.client.POST(
    "/calendars/notes",
    {
      body: toCalendarNoteRequestBody(note),
    }
  );

  return {
    data: toNote(await generatedSchedugramServerClient.unwrap(result)),
  };
}

export async function updateNote(note: NoteUpsertPayload): Promise<{
  data: NoteType;
}> {
  if (!note._id) {
    throw new Error("Cannot update calendar note without _id");
  }

  const result = await generatedSchedugramServerClient.client.PUT(
    "/calendars/notes/{id}",
    {
      params: {
        path: { id: note._id },
      },
      body: toCalendarNoteRequestBody(note),
    }
  );

  return {
    data: toNote(await generatedSchedugramServerClient.unwrap(result)),
  };
}

export async function removeNote(noteId: string): Promise<{
  data: string;
}> {
  const result = await generatedSchedugramServerClient.client.DELETE(
    "/calendars/notes/{id}",
    {
      params: {
        path: { id: noteId },
      },
      parseAs: "text",
    }
  );

  await generatedSchedugramServerClient.ensureOk(result);

  return {
    data: result.data ?? "",
  };
}

function toNote(note: CalendarNoteResponse): NoteType {
  if (!note.date) {
    throw new Error("Calendar note response is missing date");
  }

  return {
    ...note,
    date: new Date(note.date),
  };
}

function toCalendarNoteRequestBody(
  note: NoteUpsertPayload
): CalendarNoteRequestBody {
  return {
    _id: note._id,
    accountIds: note.accountIds,
    title: note.title,
    description: note.description,
    color: note.color,
    date:
      note.date instanceof Date ? note.date.toISOString() : String(note.date),
  };
}
